import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    PRESET_DAYS,
    DURATION_MODE,
    validateCustomDate,
    isDurationValid,
    describeExpiry,
    minSelectableDate,
    maxSelectableDate,
    validateNote,
    NOTE_MAX_LENGTH,
} from "../../Helpers/SuppressionDuration";

/**
 * Task 9, suppress flow step 2 — how long the suppression should last.
 *
 * Every rule here comes from Helpers/SuppressionDuration.js; this component only renders choices
 * and reports them back. In particular it does no date arithmetic of its own, so the presets, the
 * 7-180 day bounds and the resolved expiry cannot disagree with what is eventually sent.
 *
 * The picker is bounded by minSelectableDate/maxSelectableDate so an out-of-range date is hard to
 * choose, and validateCustomDate still runs on every change — the bounds are a convenience, the
 * validator is the authority, and the specification explicitly exercises rejection.
 */
export default function Step2Duration({
    duration,
    onChange,
    note,
    onNoteChange,
    selectedJobCount,
}) {
    const { mode, presetDays, customDate } = duration;
    const customCheck = mode === DURATION_MODE.CUSTOM ? validateCustomDate(customDate) : null;
    // Still needed for the inline messages below. Whether Finish is enabled is decided by the
    // dialog's footer, which applies the same two rules (isDurationValid + validateNote).
    const noteCheck = validateNote(note);

    const set = (patch) => onChange({ ...duration, ...patch });

    return (
        <div>
            <p className="text-muted">
                Choose how long this error should stay suppressed
                {selectedJobCount > 0 && (
                    <>
                        {" "}
                        on <strong>{selectedJobCount}</strong> job
                        {selectedJobCount === 1 ? "" : "s"}
                    </>
                )}
                .
            </p>

            {/* Presets */}
            <div className="mb-3">
                <label className="form-label fw-semibold">Preset durations</label>
                <div className="d-flex flex-wrap gap-2">
                    {PRESET_DAYS.map((days) => {
                        const active = mode === DURATION_MODE.PRESET && presetDays === days;
                        return (
                            <button
                                key={days}
                                type="button"
                                className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-primary"}`}
                                onClick={() => set({ mode: DURATION_MODE.PRESET, presetDays: days })}
                            >
                                {days} days
                            </button>
                        );
                    })}
                </div>
            </div>

            {/*
              Custom date. Both radios carry one shared `name`: without it they are not a radio
              group, so arrow-key navigation between them does nothing and assistive technology
              announces them as two unrelated controls.
            */}
            <div className="mb-3">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="suppression-duration-mode"
                        id="suppression-custom"
                        checked={mode === DURATION_MODE.CUSTOM}
                        onChange={() => set({ mode: DURATION_MODE.CUSTOM })}
                    />
                    <label className="form-check-label" htmlFor="suppression-custom">
                        Choose a specific date
                    </label>
                </div>
                {mode === DURATION_MODE.CUSTOM && (
                    <div style={{ marginTop: 8, marginLeft: 24 }}>
                        <DatePicker
                            selected={customDate}
                            onChange={(date) => set({ mode: DURATION_MODE.CUSTOM, customDate: date })}
                            minDate={minSelectableDate()}
                            maxDate={maxSelectableDate()}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            className="form-control"
                        />
                        {customCheck && !customCheck.valid && customDate && (
                            <div className="text-danger" style={{ marginTop: 4 }}>
                                {customCheck.message}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Indefinite */}
            <div className="mb-3">
                <div className="form-check">
                    <input
                        className="form-check-input"
                        type="radio"
                        name="suppression-duration-mode"
                        id="suppression-indefinite"
                        checked={mode === DURATION_MODE.INDEFINITE}
                        onChange={() => set({ mode: DURATION_MODE.INDEFINITE })}
                    />
                    <label className="form-check-label" htmlFor="suppression-indefinite">
                        Indefinitely — no expiry
                    </label>
                </div>
            </div>

            {/*
              C5 — optional justification, capped at 80 characters. maxLength stops most
              over-typing; validateNote is the authority, because paste and autofill can exceed it.
              Rendered back to the customer as escaped text everywhere it is displayed — never with
              dangerouslySetInnerHTML, and if it ever reaches an email template it must go through
              WebUtility.HtmlEncode like every other token in EmailTemplateRenderer.
            */}
            <div className="mb-3">
                <label className="form-label fw-semibold" htmlFor="suppression-note">
                    Reason <span className="text-muted fw-normal">(optional)</span>
                </label>
                <textarea
                    id="suppression-note"
                    className={`form-control${noteCheck.valid ? "" : " is-invalid"}`}
                    rows={2}
                    maxLength={NOTE_MAX_LENGTH}
                    value={note || ""}
                    placeholder="Why is this error being suppressed?"
                    onChange={(e) => onNoteChange(e.target.value)}
                />
                <div className="d-flex justify-content-between" style={{ marginTop: 4 }}>
                    <span className="text-danger" style={{ fontSize: 13 }}>
                        {noteCheck.message || ""}
                    </span>
                    <span className="text-muted" style={{ fontSize: 13 }}>
                        {noteCheck.length}/{NOTE_MAX_LENGTH}
                    </span>
                </div>
            </div>

            {/*
              Echo the resolved outcome back before the customer commits. Restating the actual
              date, rather than the duration they picked, is what makes a boundary or timezone
              surprise visible before it is saved instead of afterwards.
            */}
            <div className="alert alert-light border" style={{ minHeight: 52 }}>
                {isDurationValid(duration) ? (
                    describeExpiry(duration)
                ) : (
                    <span className="text-muted">Choose a duration to continue.</span>
                )}
            </div>

        </div>
    );
}
